import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Sparkles,
    Users,
    TrendingUp,
    Lightbulb,
    ChevronDown,
    ChevronUp,
} from 'lucide-react'
import { Button } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import {
    analyzeRevenueInsights,
    RevenueInsightResponse,
} from '../../../../services/gemini'

interface AIInsightsProps {
    chartData: any[]
    comparisonData?: any[]
    periodRange: string
    comparisonPeriodRange?: string
    hasComparison: boolean
}

const AIInsights = ({
    chartData,
    comparisonData,
    periodRange,
    comparisonPeriodRange,
    hasComparison,
}: AIInsightsProps) => {
    const { t } = useTranslation()
    const [insights, setInsights] = useState<RevenueInsightResponse | null>(
        null
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [expanded, setExpanded] = useState(true)
    const hasAutoAnalyzed = useRef(false)

    const handleAnalyze = async () => {
        if (chartData.length === 0) return

        setLoading(true)
        setError(null)

        try {
            const result = await analyzeRevenueInsights({
                data: chartData,
                period_range: periodRange,
                comparison_data: hasComparison ? comparisonData : undefined,
                comparison_period_range: hasComparison
                    ? comparisonPeriodRange
                    : undefined,
            })
            setInsights(result)
        } catch (err) {
            setError('Không thể phân tích dữ liệu. Vui lòng thử lại.')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    // Auto analyze when data is available
    useEffect(() => {
        if (chartData.length > 0 && !hasAutoAnalyzed.current && !insights) {
            hasAutoAnalyzed.current = true
            handleAnalyze()
        }
    }, [chartData])

    if (!insights && !loading) {
        return (
            <Paper className="bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-purple-900">
                            AI Insights
                        </span>
                        <span className="text-sm text-purple-600">
                            {t('employees.performance.revenue_insights')}
                        </span>
                    </div>
                    <Button
                        size="small"
                        onClick={handleAnalyze}
                        loading={loading}
                        disabled={chartData.length === 0}
                        className="!bg-purple-600 hover:!bg-purple-700"
                    >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {t('common.analyze')}
                    </Button>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </Paper>
        )
    }

    if (loading) {
        return (
            <Paper className="bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                    <span className="text-purple-900">
                        Đang phân tích dữ liệu...
                    </span>
                </div>
            </Paper>
        )
    }

    if (!insights) return null

    return (
        <Paper className="bg-gradient-to-r from-purple-50 to-blue-50">
            {/* Header */}
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900">
                        AI Insights
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAnalyze()
                        }}
                        loading={loading}
                        className="!text-purple-600 !border-purple-300"
                    >
                        {t('common.refresh')}
                    </Button>
                    {expanded ? (
                        <ChevronUp className="w-5 h-5 text-purple-600" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-purple-600" />
                    )}
                </div>
            </div>

            {/* Content */}
            {expanded && (
                <div className="mt-4 space-y-4">
                    {/* Summary */}
                    <div className="p-3 bg-white rounded-md">
                        <p className="text-sm text-gray-700">
                            {insights.summary}
                        </p>
                    </div>

                    {/* Customer Insights */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">
                                Phân tích nguồn khách hàng
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-sm font-medium text-green-600 mb-1">
                                    Khách hàng mới
                                </p>
                                <p className="text-sm text-gray-600">
                                    {insights.customer_insights.new_customers}
                                </p>
                            </div>
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-sm font-medium text-blue-600 mb-1">
                                    Khách hàng cũ
                                </p>
                                <p className="text-sm text-gray-600">
                                    {
                                        insights.customer_insights
                                            .retained_customers
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="p-3 bg-white rounded-md">
                            <p className="text-sm text-gray-600">
                                {insights.customer_insights.customer_analysis}
                            </p>
                        </div>
                    </div>

                    {/* Revenue Breakdown */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-sm">
                                Phân tích doanh thu
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-sm font-medium text-purple-600 mb-1">
                                    Bán mới
                                </p>
                                <p className="text-sm text-gray-600">
                                    {
                                        insights.revenue_breakdown
                                            .new_sale_analysis
                                    }
                                </p>
                            </div>
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-sm font-medium text-emerald-600 mb-1">
                                    Duy trì
                                </p>
                                <p className="text-sm text-gray-600">
                                    {
                                        insights.revenue_breakdown
                                            .renewal_analysis
                                    }
                                </p>
                            </div>
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-sm font-medium text-amber-600 mb-1">
                                    Upsell
                                </p>
                                <p className="text-sm text-gray-600">
                                    {insights.revenue_breakdown.upsell_analysis}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Comparison Insights */}
                    {insights.comparison_insights && (
                        <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                            <p className="text-sm font-medium text-blue-700 mb-1">
                                So sánh với kỳ trước
                            </p>
                            <p className="text-sm text-gray-600">
                                {insights.comparison_insights}
                            </p>
                        </div>
                    )}

                    {/* Trends & Recommendations */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Trends */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-orange-600" />
                                <span className="font-medium text-sm">
                                    Xu hướng
                                </span>
                            </div>
                            <ul className="space-y-1.5">
                                {insights.trends.map((trend, index) => (
                                    <li
                                        key={index}
                                        className="text-sm text-gray-600 flex items-start gap-1"
                                    >
                                        <span className="text-orange-500 mt-0.5">
                                            •
                                        </span>
                                        {trend}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Recommendations */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Lightbulb className="w-4 h-4 text-yellow-600" />
                                <span className="font-medium text-sm">
                                    Đề xuất
                                </span>
                            </div>
                            <ul className="space-y-1.5">
                                {insights.recommendations.map((rec, index) => (
                                    <li
                                        key={index}
                                        className="text-sm text-gray-600 flex items-start gap-1"
                                    >
                                        <span className="text-yellow-500 mt-0.5">
                                            •
                                        </span>
                                        {rec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </Paper>
    )
}

export default AIInsights
